import { getParameterFromURL } from './getParameterFromUrl';

/**
 * Single source of truth for the currency symbol shown across the app.
 *
 * The currency is derived from immutable URL params, so plain functions (not the
 * store) are enough: they are usable from React components, module-level config
 * (getPricesConfig) and non-React utils (PayPal redirect) alike.
 *
 * - `?subscrUK`  → £
 * - `?subscrEU`  → €
 * - `?currencyInISO` → "USD " prefix (display variant)
 */

export const isSubscrUK = (): boolean => Boolean(getParameterFromURL('subscrUK'));

export const isSubscrEU = (): boolean => Boolean(getParameterFromURL('subscrEU'));

export const isCurrencyInISO = (): boolean => Boolean(getParameterFromURL('currencyInISO'));

/** Display currency prefix: £ / € / "USD " / $. The default app-wide variant. */
export const getCurrencySymbol = (): string =>
  isSubscrUK() ? '£' : isSubscrEU() ? '€' : isCurrencyInISO() ? 'USD ' : '$';

/** Payment-context currency (resolveTariffPayment, prices config): £ / € / $ — no ISO prefix. */
export const getPaymentCurrency = (): string => (isSubscrUK() ? '£' : isSubscrEU() ? '€' : '$');

/** Chat-upsale / PDF-upsell variant: ignores the UK/EU region params on purpose. */
export const getUsdCurrencyPrefix = (): string => (isCurrencyInISO() ? 'USD ' : '$');

/**
 * PDF-upsell variant: with `?currencyInISO` returns the region's ISO code prefix
 * ("GBP " / "EUR " / "USD "), otherwise the plain symbol.
 */
export const getRegionalCurrencyPrefix = (): string => {
  const iso = isCurrencyInISO();
  if (isSubscrUK()) return iso ? 'GBP ' : '£';
  if (isSubscrEU()) return iso ? 'EUR ' : '€';
  return iso ? 'USD ' : '$';
};

/**
 * Extended variant that maps the actual ISO code carried by `?currencyInISO`
 * (EUR / GBP / USD) instead of always falling back to "USD ".
 */
export const getCurrencySymbolFromISOCode = (): string => {
  if (isSubscrUK()) return '£';
  if (isSubscrEU()) return '€';

  const iso = getParameterFromURL('currencyInISO');
  if (iso === 'EUR') return '€';
  if (iso === 'GBP') return '£';
  if (iso === 'USD') return 'USD ';

  return '$';
};
